<script>
  // src/MapScripting.svelte
  import { getContext, onMount, onDestroy, createEventDispatcher } from "svelte";
  import { contextKey } from "@beyonk/svelte-mapbox";
  import { fade, fly } from "svelte/transition";
  import { addPOIs, removePois, addNumericPOIs, removeNumericPois } from "./mapPois";
  import { addBuildings } from "./mapBuildings";
  import { addVisualLayer } from "./mapVisualLayer";
  // import { createFilterFromFeatures } from "./mapFilter";
  import MapAccessibility from "./MapAccessibility.svelte";

  import UA from "ua-parser-js";
  const ua = new UA();
  let isMobile = ua.getResult().device["type"] == "mobile";
  // Exports
  export let tour;
  export let expanded;
  export let mapLocationsVisible = false;

  let tooltipX = 0;
  let tooltipY = 0;
  let tooltipText;

  const { getMap, getMapbox } = getContext(contextKey);
  const map = getMap();

  const dispatch = createEventDispatcher();

  // function drawFilterFeatures(features, color = "#ff0000") {
  //   const sourceName = `filter-${Math.random().toString(36).substring(7)}`;
  //   map.addSource(sourceName, {
  //     type: "geojson",
  //     data: {
  //       type: "FeatureCollection",
  //       features,
  //     },
  //   });
  //   map.addLayer({
  //     id: sourceName,
  //     type: "line",
  //     source: sourceName,
  //     paint: {
  //       "line-color": color,
  //       "line-width": 2,
  //     },
  //   });
  // }
  // const hasInclusionFilter = tour?.mapbox?.inclusionFilterFeatures?.length > 0;
  // const hasExclusionFilter = tour?.mapbox?.exclusionFilterFeatures?.length > 0;
  // const excludeLayers = [
  //   "clusters",
  //   "cluster-count",
  //   "unclustered-point",
  //   "tour-buildings-layer",
  //   "tour-bg-buildings-layer",
  //   "tour-buildings-layer-labels",
  //   "tour-bg-buildings-layer-labels",
  //   "visual-layer-labels",
  //   "visual-layer",
  // ];

  // function applyFilters() {
  //   let layers = map.getStyle().layers;
  //   if (hasInclusionFilter || hasExclusionFilter) {
  //     layers = layers.filter((layer) => !excludeLayers.includes(layer.id));
  //     layers.forEach((layer) => {
  //       if (hasInclusionFilter) {
  //         map.setFilter(
  //           layer.id,
  //           createFilterFromFeatures(tour?.mapbox?.inclusionFilterFeatures),
  //         );
  //       }
  //       if (hasExclusionFilter) {
  //         map.setFilter(
  //           layer.id,
  //           createFilterFromFeatures(
  //             tour?.mapbox?.exclusionFilterFeatures,
  //             false,
  //           ),
  //         );
  //       }
  //     });
  //   }
  // }

  // function highlightExcludedFeatures() {
  //   if (hasExclusionFilter) {
  //     const exclusionFilter = createFilterFromFeatures(
  //       tour?.mapbox?.exclusionFilterFeatures,
  //       false,
  //     );
  //     map.addLayer({
  //       id: "excluded-features",
  //       type: "fill",
  //       source: "composite",
  //       filter: exclusionFilter,
  //       paint: {
  //         "fill-color": "#ff0000",
  //         "fill-opacity": 0.5,
  //       },
  //     });
  //   }
  // }

  function setTooltip(tooltip) {
    if (!tooltip) {
      tooltipX = 0;
      tooltipY = 0;
      tooltipText = false;
      return;
    }

    const { x, y, text } = tooltip;
    tooltipX = x;
    tooltipY = y;
    tooltipText = text;
  }

  onMount(async () => {
    map.on("zoom", () => {
      console.log("zoom", map.getZoom());
    });

    map.on("click", function (e) {
      console.log("event:", e);
      const center = map.getCenter();
      const json = {
        mapCenter: [center.lng, center.lat],
        mapBearing: map.getBearing(),
        mapPitch: map.getPitch(),
        zoomExpandedMap: map.getZoom(),
      };
      console.log(json);
    });

    if (tour?.mapbox?.visualFeatures?.length > 0) {
      const { visualFeatures, visualFeaturesColor } = tour?.mapbox;

      const layers = map.getStyle().layers;
      const desiredLayer = "waterway";
      const beforeLayerId = layers.find((l) => l.id === desiredLayer)?.id;

      addVisualLayer({
        map,
        beforeLayerId,
        visualFeatures,
        visualFeaturesColor,
      });
    }

    if (tour?.mapbox?.buildings?.length > 0) {
      const {
        mapboxTilesetUrl: tilesetUrl,
        mapboxTilesetLayerName: tilesetLayerName,
        labelsUrl,
        labelsLayerName,
        buildingColor,
        buildingHoverColor,
        buildings,
      } = tour?.mapbox;

      addBuildings({
        map,
        tilesetUrl,
        tilesetLayerName,
        labelsUrl,
        labelsLayerName,
        buildingColor,
        buildings,
        buildingHeight: 7,
        buildingHoverColor,
        setTooltip,
        scenes: tour?.scenes,
      });
    }
  });

  $: if (expanded) {
    if (tour?.mapbox?.pois?.length > 0) {
      const { pois, poiBgColor, poiTextColor } = tour?.mapbox;
      const bgColor = poiBgColor || tour?.theme?.primaryColor;
      const textColor = poiTextColor || "#fff";

      addPOIs({
        map,
        setTooltip,
        pois,
        bgColor,
        textColor,
        scenes: tour?.scenes,
      });
    }

    if (tour?.mapbox?.numericPois?.length > 0) {
      const { numericPois, numericPoiBgColor, numericPoiTextColor } = tour?.mapbox;
      const bgColor = numericPoiBgColor || tour?.theme?.primaryColor;
      const textColor = numericPoiTextColor || "#fff";
      addNumericPOIs({
        map,
        setTooltip,
        pois: numericPois,
        bgColor,
        textColor,
        scenes: tour?.scenes,
      });
    }
  } else {
    removePois({ map });
    removeNumericPois({ map });
  }

  onDestroy(() => {
    // Clean up POIs and event listeners
    if (map) {
      removePois({ map });
      removeNumericPois({ map });
    }
  });
</script>

{#if expanded}
  <MapAccessibility {tour} {map} bind:isVisible={mapLocationsVisible} />
{/if}

{#if !isMobile && tooltipText}
  <div
    class="tooltip"
    style={`top: ${tooltipY - 50}px; left: ${tooltipX - 50}px;`}
  >
    {tooltipText}
  </div>
{/if}

<style>
  :root {
    --tooltip-background: #000;
    --tooltip-color: #fff;
    --tooltip-padding: 0.5rem;
    --tooltip-border-radius: 15px;
  }

  .tooltip {
    position: absolute;
    background: var(--tooltip-background);
    color: var(--tooltip-color);
    padding: var(--tooltip-padding);
    z-index: 999;
    border-radius: var(--tooltip-border-radius);
  }
</style>
